import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import {
  Wind, Fuel, Zap, Thermometer, Gauge, AlertTriangle, Wrench,
  ChevronDown, ChevronUp, Flame, Activity, ShieldAlert, Sparkles, Search
} from 'lucide-react';

const ISSUES = [
  {
    id: 'vacuum-leak',
    title: 'Vacuum Leak',
    severity: 'common',
    icon: Wind,
    symptoms: ['Rough or hunting idle (RPM swings 100+ RPM)', 'Lean AFR at idle (>15.5)', 'Positive LTFT (ECU adding fuel to compensate)', 'Hissing sound from engine bay', 'Check engine light (lean codes P0171/P0174)'],
    causes: ['Cracked or disconnected vacuum hoses', 'Valve cover gasket failure (very common on BMW N51/N52)', 'Intake manifold gasket leak', 'PCV valve failure', 'Torn intake boot between MAF and throttle body'],
    diagnosis: ['Smoke test — mechanic pumps smoke into intake, leaks become visible', 'Spray brake cleaner around intake connections while idling — RPM will change at the leak point', 'Listen for hissing with engine running', 'Check LTFT: B1 and B2 both positive = intake leak, only one bank = exhaust leak near O2'],
    fix: ['Replace valve cover gasket ($30-80 part, 2-4 hours labor)', 'Replace cracked vacuum lines ($5-20 each)', 'Replace PCV valve ($20-40)', 'Replace intake boot ($40-80)', 'After repair: clear adaptations, drive 30 min, re-log'],
    impact_on_tuning: 'A vacuum leak adds unmetered air the ECU doesn\'t know about. This makes every datalog inaccurate — the AI will tune based on wrong data. Fix leaks FIRST.',
  },
  {
    id: 'fuel-trims',
    title: 'Excessive Fuel Trims (LTFT > ±12%)',
    severity: 'common',
    icon: Fuel,
    symptoms: ['LTFT consistently above +10% (lean) or below -10% (rich)', 'Poor fuel economy', 'Hesitation on acceleration', 'Rough idle that smooths out once warm'],
    causes: ['Vacuum leak (positive trims)', 'Dirty or failing MAF sensor', 'Clogged fuel injectors', 'Weak fuel pump (low fuel pressure)', 'Exhaust leak near O2 sensor (false lean reading)', 'Leaking fuel injector (negative trims)'],
    diagnosis: ['Compare LTFT B1 vs B2 — if both high, problem is shared (intake/MAF). If one bank only, problem is bank-specific (injector/exhaust).', 'Check STFT at idle vs cruising — high at idle points to vacuum leak, high at cruise points to MAF/fuel delivery', 'Monitor fuel pressure with a gauge under load'],
    fix: ['Clean MAF sensor with MAF-specific cleaner ($10)', 'Replace fuel filter ($20-50)', 'Professional injector cleaning or replacement', 'Test fuel pressure — should be 50-60 PSI on most port injection systems', 'After fixing: clear adaptations, drive 30+ min to relearn'],
    impact_on_tuning: 'High fuel trims mean the base calibration is already wrong. Tuning on top of bad trims compounds the error. Get trims under ±8% before tuning.',
  },
  {
    id: 'knock',
    title: 'Engine Knock / Detonation',
    severity: 'critical',
    icon: Zap,
    symptoms: ['Knock sensor voltage spikes (>2.0V)', 'ECU pulling timing (negative timing values under load)', 'Pinging or rattling sound under acceleration', 'Reduced power under load'],
    causes: ['Low octane fuel', 'Carbon buildup on pistons/valves (common on direct injection)', 'Overheating / cooling system failure', 'Wrong spark plugs (too hot heat range)', 'Excessive timing advance in tune', 'Running E85 with gasoline-calibrated tune'],
    diagnosis: ['Log knock sensor voltage per cylinder — real knock varies between cylinders', 'If ALL cylinders show identical knock voltage, it\'s likely electrical noise (sensor/wiring issue)', 'Check timing retard — ECU pulls timing 3-10° when knock is detected', 'Verify coolant temp stays below 230°F under load'],
    fix: ['Use correct fuel for your tune (91+ octane or E85 as calibrated)', 'Walnut blast intake valves on direct injection engines ($300-500)', 'Replace spark plugs with correct heat range ($60-120 for 6-cyl)', 'Check coolant level and thermostat', 'If knock persists, reduce timing advance in tune'],
    impact_on_tuning: 'STOP TUNING if knock is detected. Detonation destroys engines. Fix the cause first — fuel quality, spark plugs, carbon buildup, or cooling.',
  },
  {
    id: 'idle-timing',
    title: 'Negative Idle Timing',
    severity: 'warning',
    icon: Gauge,
    symptoms: ['Average idle timing below 0°', 'Timing swinging wildly at idle (-5° to +15°)', 'Rough idle, vibration at stop lights'],
    causes: ['Combustion instability from vacuum leak', 'Carbon buildup causing hot spots', 'Wrong spark plugs', 'EGR valve stuck open (if equipped)', 'Intake valve deposits (direct injection)'],
    diagnosis: ['Log timing on all cylinders — if one is consistently lower, suspect that cylinder\'s coil or plug', 'Check if timing recovers to positive during light cruise (it should)', 'Verify no misfire codes stored'],
    fix: ['Replace spark plugs and check coils', 'Walnut blast intake valves', 'Check EGR valve operation', 'Ensure proper fuel octane', 'After fix: clear adaptations, re-log idle'],
    impact_on_tuning: 'Negative idle timing means the ECU is fighting detonation at idle. Adding more timing via tuning will make it worse. Fix the root cause.',
  },
  {
    id: 'lean-wot',
    title: 'Lean Under Load (WOT AFR >15)',
    severity: 'critical',
    icon: Flame,
    symptoms: ['AFR above 15.0 at wide-open throttle', 'Loss of power at high RPM', 'Engine feels "flat" under hard acceleration', 'Possible knock events'],
    causes: ['Insufficient fuel map scaling for E85', 'Weak fuel pump can\'t deliver enough volume at high demand', 'Clogged fuel filter restricting flow', 'Cal-block-only flash missing WOT lambda targets', 'Fuel injectors maxed out (need larger injectors)', 'Boost leak (turbo cars)'],
    diagnosis: ['Check if AFR target matches actual — if target is 14.7 and actual is 15+, it\'s a calibration issue', 'If target is 12.0 and actual is 15+, it\'s a delivery issue (pump/injectors/filter)', 'Log fuel pressure under load — should not drop more than 5 PSI', 'For modified tunes: verify FULL FLASH was used, not cal-block only'],
    fix: ['ALWAYS use Full Flash (not cal-block) to include WOT lambda targets', 'Replace fuel filter ($20-50)', 'Upgrade fuel pump if running E85 (20-30% more fuel volume needed)', 'Professional injector flow testing', 'Increase fuel map scaling in tune (AI will guide this)'],
    impact_on_tuning: 'Running lean under load causes detonation and engine damage. This MUST be resolved before adding timing or power.',
  },
  {
    id: 'misfire',
    title: 'Misfires',
    severity: 'warning',
    icon: AlertTriangle,
    symptoms: ['Rough running at specific RPM ranges', 'Check engine light with P030x codes', 'Loss of power on one or more cylinders', 'Fuel smell from exhaust', 'Flashing check engine light (active misfire)'],
    causes: ['Worn spark plugs (most common)', 'Failing ignition coil', 'Fuel injector sticking closed', 'Low compression on one cylinder', 'Vacuum leak on one intake runner'],
    diagnosis: ['Read misfire counter per cylinder (Mode 6 data)', 'Swap coil from misfiring cylinder to known good — if misfire follows, it\'s the coil', 'Same test for spark plug', 'Compression test if plug and coil are good'],
    fix: ['Replace spark plugs — use OEM spec ($10-20 each)', 'Replace failing ignition coil ($30-80 each)', 'Clean or replace fuel injector', 'If compression is low: leak-down test to identify head gasket, valve, or ring issue'],
    impact_on_tuning: 'Misfires create unburned fuel that contaminates the catalytic converter and O2 sensors. Tune quality will be poor until misfires are resolved.',
  },
  {
    id: 'cooling',
    title: 'Overheating / Cooling Issues',
    severity: 'warning',
    icon: Thermometer,
    symptoms: ['Coolant temp above 230°F under load', 'Temperature climbing during spirited driving', 'Coolant loss without visible leak', 'Steam from overflow', 'Knock events that appear only when hot'],
    causes: ['Low coolant level', 'Failed thermostat (stuck closed)', 'Electric water pump failure (common on BMW N51/N52)', 'Clogged radiator', 'Failed radiator fan', 'Head gasket leak (coolant into combustion chamber)'],
    diagnosis: ['Monitor coolant temp during a WOT pull — should stay below 220°F', 'Check for coolant in oil (milky appearance on dipstick)', 'Pressure test cooling system', 'Verify electric fan kicks on at ~200°F'],
    fix: ['Top off coolant and bleed air from system', 'Replace thermostat ($30-60)', 'Replace electric water pump ($200-400 for BMW)', 'Flush radiator or replace ($150-300)', 'Check fan relay and wiring'],
    impact_on_tuning: 'Hot engines knock. The ECU will pull timing to protect itself, which masks any tuning gains. Keep coolant temps under 220°F for consistent results.',
  },
];

const SEVERITY_CONFIG = {
  critical: { color: 'text-red-400', bg: 'bg-red-400/10', border: 'border-red-500/20', label: 'Critical' },
  common: { color: 'text-amber-400', bg: 'bg-amber-400/10', border: 'border-amber-500/20', label: 'Common' },
  warning: { color: 'text-yellow-400', bg: 'bg-yellow-400/10', border: 'border-yellow-500/20', label: 'Warning' },
};

function IssueCard({ issue }) {
  const [expanded, setExpanded] = useState(false);
  const Icon = issue.icon;
  const sev = SEVERITY_CONFIG[issue.severity];

  return (
    <Card className={`bg-[#111111] border-[#27272A] rounded-sm hover:border-[#FF5E1A]/15 transition-colors`} data-testid={`issue-${issue.id}`}>
      <button onClick={() => setExpanded(!expanded)} className="w-full text-left p-5">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 flex items-center justify-center rounded-sm ${sev.bg} border ${sev.border} flex-shrink-0`}>
            <Icon className={`w-5 h-5 ${sev.color}`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              <h3 className="text-sm font-bold text-white font-['Outfit']">{issue.title}</h3>
              <Badge className={`${sev.bg} ${sev.color} text-[8px] px-1.5 py-0 rounded-sm border ${sev.border}`}>{sev.label}</Badge>
            </div>
            <p className="text-xs text-zinc-500 truncate">{issue.symptoms[0]}</p>
          </div>
          {expanded ? <ChevronUp className="w-4 h-4 text-zinc-500" /> : <ChevronDown className="w-4 h-4 text-zinc-500" />}
        </div>
      </button>

      {expanded && (
        <CardContent className="pt-0 pb-5 space-y-4">
          <Section title="Symptoms" items={issue.symptoms} icon={<AlertTriangle className="w-3 h-3 text-amber-400" />} />
          <Section title="Common Causes" items={issue.causes} icon={<Search className="w-3 h-3 text-cyan-400" />} />
          <Section title="How to Diagnose" items={issue.diagnosis} icon={<Activity className="w-3 h-3 text-purple-400" />} />
          <Section title="How to Fix" items={issue.fix} icon={<Wrench className="w-3 h-3 text-[#FF5E1A]" />} />

          <div className="p-3 bg-[#0A0A0A] border border-[#FF5E1A]/15 rounded-sm">
            <div className="flex items-start gap-2">
              <Sparkles className="w-3.5 h-3.5 text-[#FF5E1A] mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-[10px] text-[#FF5E1A] uppercase tracking-wider font-bold mb-1">Impact on Tuning</p>
                <p className="text-xs text-zinc-300">{issue.impact_on_tuning}</p>
              </div>
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  );
}

function Section({ title, items, icon }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-2">
        {icon}
        <p className="text-[10px] text-zinc-500 uppercase tracking-wider font-bold">{title}</p>
      </div>
      <ul className="space-y-1.5 ml-5">
        {items.map((item, i) => (
          <li key={i} className="text-xs text-zinc-300 leading-relaxed list-disc">{item}</li>
        ))}
      </ul>
    </div>
  );
}

export default function MechanicalGuide() {
  return (
    <div className="space-y-5" data-testid="mechanical-guide">
      <Card className="bg-[#0A0A0A] border-[#FF5E1A]/20 rounded-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-white font-['Outfit'] text-base">
            <Wrench className="w-5 h-5 text-[#FF5E1A]" />Common Mechanical Issues & Fixes
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-zinc-400 leading-relaxed mb-2">
            Tuning an unhealthy engine is like building a house on sand. These are the most common issues
            we see in datalogs — fixing them first makes tuning faster, safer, and more effective.
          </p>
          <p className="text-xs text-zinc-600">Click any issue to see symptoms, causes, diagnosis steps, and fixes.</p>
        </CardContent>
      </Card>

      <div className="space-y-3">
        {ISSUES.map(issue => (
          <IssueCard key={issue.id} issue={issue} />
        ))}
      </div>

      <div className="p-4 bg-[#0A0A0A] border border-yellow-500/15 rounded-sm">
        <div className="flex items-start gap-2">
          <ShieldAlert className="w-4 h-4 text-yellow-500/70 mt-0.5 flex-shrink-0" />
          <p className="text-[11px] text-zinc-500 leading-relaxed">
            <span className="text-yellow-500/80 font-bold">Disclaimer:</span> This guide is for educational purposes. Always consult a qualified mechanic for diagnosis and repair. AI Tuner Pro flags potential issues but cannot replace hands-on inspection. You assume full responsibility for all vehicle modifications.
          </p>
        </div>
      </div>
    </div>
  );
}
